import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, UserPlus, AlertTriangle, FileWarning } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import UserPicker from './components/UserPicker';
import PaginationComponent from '../../../shared/components/PaginationComponent';
import {
  getEmployees,
  getEmployeeCandidates,
  createEmployee,
  createEmployeeWithUser,
  getAllDesignations,
  getExpiringDocuments
} from '../../services/api/implementation/impl';
import {
  StatusPill,
  toneForStatus
} from '../../../shared/components/ui/primitives';

const EMPLOYMENT_TYPES = [
  'PERMANENT',
  'PROBATION',
  'CONTRACT',
  'INTERN',
  'CONSULTANT'
];

const STATUSES = [
  { value: '', label: 'Active staff' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ON_NOTICE', label: 'On notice' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'EXITED', label: 'Exited' }
];

/**
 * Employee directory.
 *
 * Lists HR records, not User accounts. Most Users on this platform are
 * customers, contractors or vendors, so "add" starts from the candidates list —
 * company users who have a login but no HR record yet — rather than offering a
 * free-text form that could invent a person twice.
 */
const EmployeeDirectory = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/workforce?tab=employees' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [onboardOpen, setOnboardOpen] = useState(false);
  const limit = 25;

  const { data, isFetching } = useQuery({
    queryKey: ['employees', page, search, status],
    queryFn: () => getEmployees({ page, limit, search, status }),
    keepPreviousData: true
  });

  const expiring = useQuery({
    queryKey: ['expiringDocuments'],
    queryFn: () => getExpiringDocuments(60)
  });

  const rows = data?.data ?? [];
  const meta = data?.meta ?? {};
  const totalPages = meta.total ? Math.ceil(meta.total / limit) : 1;
  const expiringRows = expiring.data?.data ?? [];

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'Employees' }} />

      <div className="p-4 space-y-4">
        {expiringRows.length > 0 && (
          <div className="flex items-start gap-2 rounded-control border border-warning-border bg-warning-bg px-3 py-2 text-xs text-warning-fg">
            <FileWarning size={14} className="mt-0.5 shrink-0" />
            <div>
              <strong>
                {expiringRows.length} document{expiringRows.length === 1 ? '' : 's'}{' '}
                expired or expiring in the next 60 days.
              </strong>
              <div className="mt-1 text-xs space-y-0.5">
                {expiringRows.slice(0, 4).map((doc) => (
                  <div key={doc.id}>
                    {doc.employee_name} — {titleCase(doc.doc_type)}{' '}
                    {doc.expired ? (
                      <span className="font-semibold">expired</span>
                    ) : (
                      <>expires {formatDate(doc.expires_on)}</>
                    )}
                  </div>
                ))}
                {expiringRows.length > 4 && (
                  <div className="italic">…and {expiringRows.length - 4} more.</div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search by name"
              className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm"
            />
          </div>

          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => setOnboardOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-700 text-white text-sm font-medium"
          >
            <UserPlus size={16} /> Onboard employee
          </button>
        </div>

        {isFetching ? (
          <Loader />
        ) : (
          <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <Th>Name</Th>
                  <Th>Code</Th>
                  <Th>Designation</Th>
                  <Th>Vertical</Th>
                  <Th>Type</Th>
                  <Th>Joined</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-10 text-gray-500">
                      No employee records yet. Use “Onboard employee” to create
                      one for an existing user.
                    </td>
                  </tr>
                )}
                {rows.map((employee) => (
                  <tr
                    key={employee.id}
                    className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/builder/hr/employees/${employee.id}`)}
                  >
                    <Td>
                      <div className="font-medium text-gray-900">{employee.name}</div>
                      <div className="text-xs text-gray-500">{employee.email}</div>
                    </Td>
                    <Td>{employee.employee_code ?? '—'}</Td>
                    <Td>{employee.designation ?? '—'}</Td>
                    <Td>{employee.vertical ?? '—'}</Td>
                    <Td>{titleCase(employee.employment_type)}</Td>
                    <Td>{formatDate(employee.date_of_joining)}</Td>
                    <Td>
                      <EmploymentPill employee={employee} />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <PaginationComponent
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        )}
      </div>

      {onboardOpen && (
        <OnboardModal
          onClose={() => setOnboardOpen(false)}
          onCreated={() => {
            queryClient.invalidateQueries({ queryKey: ['employees'] });
            setOnboardOpen(false);
          }}
        />
      )}
    </Layout>
  );
};

/**
 * Two ways in. "Has a login already" picks a company user who has no HR
 * record yet. "New person" is for everyone else — a peon, a guard, a driver,
 * an engineer on the payroll who uses nothing of the ERP: the server creates
 * a Staff login (own attendance, leave, payslips, policies; nothing more) and
 * the HR record together, and they sign in on the app with their mobile.
 */
const OnboardModal = ({ onClose, onCreated }) => {
  const [mode, setMode] = useState('new');
  const [userId, setUserId] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [employmentType, setEmploymentType] = useState('PERMANENT');
  const [joining, setJoining] = useState('');
  const [code, setCode] = useState('');

  const designations = useQuery({
    queryKey: ['designations'],
    queryFn: getAllDesignations,
    enabled: mode === 'new'
  });
  const titles = Array.from(
    new Set(
      (designations.data?.data ?? designations.data ?? [])
        .map((d) => d?.name)
        .filter(Boolean)
    )
  ).sort();

  const { data, isFetching } = useQuery({
    queryKey: ['employeeCandidates'],
    queryFn: getEmployeeCandidates
  });

  const create = useMutation({
    mutationFn: createEmployee,
    onSuccess: () => {
      toast.success('Employee record created');
      onCreated();
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not create the record')
  });
  const createNew = useMutation({
    mutationFn: createEmployeeWithUser,
    onSuccess: (saved) => {
      toast.success(
        `${saved?.user?.name ?? 'Onboarded'} — they sign in on the app with ${saved?.user?.phone_number ?? 'their mobile'}`,
        { duration: 7000 }
      );
      onCreated();
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not onboard this person')
  });

  const candidates = data?.data ?? [];
  const phoneOk = /^[6-9]\d{9}$/.test(phone.replace(/\D/g, '').replace(/^91(?=\d{10}$)/, ''));
  const busy = create.isPending || createNew.isPending;

  const submit = () => {
    const common = {
      employment_type: employmentType,
      date_of_joining: joining || null,
      employee_code: code || null
    };
    if (mode === 'new') {
      if (name.trim().length < 2) return toast.error("Enter the person's name");
      if (!phoneOk) return toast.error('Enter a 10-digit mobile number');
      return createNew.mutate({
        name: name.trim(),
        phone_number: phone,
        email: email.trim() || null,
        designation: title.trim() || null,
        ...common
      });
    }
    if (!userId) return toast.error('Choose a user');
    return create.mutate({ user_id: Number(userId), ...common });
  };

  const modeClass = (active) =>
    `flex-1 rounded-md border px-3 py-1.5 text-sm font-medium ${
      active ? 'bg-[#2A565E] text-white border-[#2A565E]' : 'bg-white text-gray-600 border-gray-300'
    }`;

  return (
    <Modal name="Onboard employee" onClose={onClose} width="560px">
      <div className="space-y-3">
        <div className="flex gap-2" role="tablist" aria-label="Who is being onboarded">
          <button type="button" role="tab" aria-selected={mode === 'new'} className={modeClass(mode === 'new')} onClick={() => setMode('new')}>
            New person
          </button>
          <button type="button" role="tab" aria-selected={mode === 'existing'} className={modeClass(mode === 'existing')} onClick={() => setMode('existing')}>
            Has a login already
          </button>
        </div>

        {mode === 'new' ? (
          <>
            <p className="text-xs text-gray-500">
              Anyone on the payroll without a login. They get an app login for their own attendance,
              leave and payslips.
            </p>
            <Field label="Full name">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="As on their ID"
              />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Mobile number">
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  inputMode="numeric"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm tabular-nums"
                  placeholder="10 digits — used to sign in"
                />
              </Field>
              <Field label="Email (optional)">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </Field>
            </div>
            <Field label="Job title">
              <input
                type="text"
                list="staff-titles"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="e.g. Security Guard, Office Assistant, Software Engineer"
              />
              <datalist id="staff-titles">
                {titles.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
            </Field>
          </>
        ) : (
          <>
            <p className="text-xs text-gray-500">
              Company users who have a login but no employee record yet.
            </p>
            <Field label="User">
              {isFetching ? (
                <Loader />
              ) : candidates.length === 0 ? (
                <p className="text-sm text-gray-500 border border-gray-200 rounded-lg px-3 py-2">
                  Every company user already has an employee record.
                </p>
              ) : (
                <UserPicker candidates={candidates} value={userId} onChange={setUserId} />
              )}
            </Field>
          </>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="Employment type">
          <select
            value={employmentType}
            onChange={(e) => setEmploymentType(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            {EMPLOYMENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {titleCase(t)}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Date of joining">
          <input
            type="date"
            value={joining}
            onChange={(e) => setJoining(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </Field>

        <Field label="Employee code">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Optional"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </Field>

        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded border border-gray-300 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || (mode === 'existing' && candidates.length === 0)}
            onClick={submit}
            className="px-4 py-2 rounded bg-brand-700 text-white text-sm disabled:opacity-50"
          >
            {busy ? 'Saving…' : mode === 'new' ? 'Onboard & create login' : 'Create record'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

/**
 * Shows the HR status, and flags when it disagrees with the login. The two are
 * deliberately independent, but an active employee whose login is disabled is
 * usually somebody's mistake and worth surfacing.
 */
/**
 * Employment state, plus the one case worth shouting about: an ACTIVE
 * employee whose login is switched off. Colour comes from the shared
 * vocabulary; only the wording is this screen's.
 */
const EmploymentPill = ({ employee }) => {
  const LABELS = {
    ACTIVE: 'Active',
    ON_NOTICE: 'On notice',
    SUSPENDED: 'Suspended',
    EXITED: 'Exited'
  };
  const mismatch = employee.status === 'ACTIVE' && employee.login_active === false;

  return (
    <div className="flex items-center gap-1">
      <StatusPill dense tone={toneForStatus(employee.status)}>
        {LABELS[employee.status] ?? '\u2014'}
      </StatusPill>
      {mismatch && (
        <span title="Active employee whose login is disabled">
          <AlertTriangle size={13} className="text-warning-fg" />
        </span>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

function titleCase(value) {
  if (!value) return '—';
  return value
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

const Th = ({ children }) => (
  <th className="text-left font-medium px-4 py-3 whitespace-nowrap">{children}</th>
);
const Td = ({ children }) => <td className="px-4 py-3 align-top">{children}</td>;

export default EmployeeDirectory;
